import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext(null);
const API_BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:4000').replace(/\/$/, '');
const API_TOKEN = process.env.REACT_APP_API_TOKEN || '';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [authReady, setAuthReady] = useState(false);

  // Restore session from localStorage on mount, then refresh from backend
  useEffect(() => {
    const stored = localStorage.getItem('titler_auth');
    if (!stored) {
      setAuthReady(true);
      return;
    }
    try {
      const parsed = JSON.parse(stored);
      if (!parsed.email) {
        localStorage.removeItem('titler_auth');
        setAuthReady(true);
        return;
      }

      // Fetch fresh user data using API token
      (async () => {
        try {
          const res = await fetch(`${API_BASE_URL}/register`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${API_TOKEN}`,
              'X-User-Email': parsed.email,
            },
          });
          if (res.ok) {
            const data = await res.json();
            const updated = {
              ...parsed,
              isPro: data.isPro ?? (data.tier === 'pro'),
              tier: data.tier || 'free',
              requestLimit: data.requestLimit ?? 0,
            };
            setUser(updated);
            localStorage.setItem('titler_auth', JSON.stringify(updated));
          } else {
            // Failed — use cached data as fallback
            setUser(parsed);
          }
        } catch {
          // Offline or backend down — use cached data as fallback
          setUser(parsed);
        }
        setAuthReady(true);
      })();
    } catch {
      localStorage.removeItem('titler_auth');
      setAuthReady(true);
    }
  }, []);

  const login = useCallback((userData) => {
    const data = {
      credential: userData.credential,
      name: userData.name || '',
      email: userData.email || '',
      picture: userData.picture || '',
      isPro: userData.isPro ?? false,
      tier: userData.tier || 'free',
      requestLimit: userData.requestLimit ?? 0,
      isNewUser: userData.isNewUser || false,
    };
    setUser(data);
    localStorage.setItem('titler_auth', JSON.stringify(data));
  }, []);

  const updateUser = useCallback((updates) => {
    setUser(prev => {
      if (!prev) return prev;
      const updated = { ...prev, ...updates };
      localStorage.setItem('titler_auth', JSON.stringify(updated));
      return updated;
    });
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    localStorage.removeItem('titler_auth');
  }, []);

  return (
    <AuthContext.Provider value={{ user, login, logout, updateUser, authReady }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
